import React, { useState, useEffect } from 'react';
import {
    FlaskConical,
    Plus,
    Search,
    ChevronDown,
    Eye,
    FileText,
    Clock,
    AlertCircle,
    CheckCircle,
    XCircle,
    Send,
    Trash2,
    X
} from 'lucide-react';
import { API_URL } from '../config/api';
import './PatientStudies.css';

const STATUS_CONFIG = {
    'solicitud_medico': { label: 'Solicitud Médica', color: '#f97316', icon: Clock },
    'solicitado': { label: 'Solicitado', color: '#083677', icon: Clock },
    'pendiente_muestra': { label: 'Pendiente Muestra', color: '#f59e0b', icon: AlertCircle },
    'muestra_tomada': { label: 'Muestra Tomada', color: '#8b5cf6', icon: CheckCircle },
    'enviado': { label: 'Enviado a Lab', color: '#3b82f6', icon: Send },
    'en_laboratorio': { label: 'En Laboratorio', color: '#6366f1', icon: FlaskConical },
    'finalizado': { label: 'Finalizado', color: '#00BBBF', icon: CheckCircle },
    'cancelado': { label: 'Cancelado', color: '#ef4444', icon: XCircle }
};

const FILTER_STATUS_OPTIONS = [
    { value: 'solicitado', label: 'Solicitado' },
    { value: 'pendiente_muestra', label: 'Pendiente Muestra' },
    { value: 'muestra_tomada', label: 'Muestra Tomada' },
    { value: 'enviado', label: 'Enviado a Lab' },
    { value: 'en_laboratorio', label: 'En Laboratorio' },
    { value: 'finalizado', label: 'Finalizado' },
    { value: 'cancelado', label: 'Cancelado' }
];

const PatientStudies = ({ patientId, patientName, onAssignStudy }) => {
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: '',
        from_date: '',
        to_date: ''
    });
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Cancel study state
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [studyToCancel, setStudyToCancel] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        fetchStudies();
    }, [patientId]);

    const fetchStudies = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const queryParams = new URLSearchParams();
            if (filters.status) queryParams.append('status', filters.status);
            if (filters.category) queryParams.append('category', filters.category);
            if (filters.from_date) queryParams.append('from_date', filters.from_date);
            if (filters.to_date) queryParams.append('to_date', filters.to_date);

            const res = await fetch(
                `${API_URL}/patients/${patientId}/studies?${queryParams}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await res.json();
                setStudies(data);
            }
        } catch (error) {
            console.error('Error fetching studies:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        if (patientId) {
            fetchStudies();
        }
    }, [filters]);

    const viewStudyDetail = async (study) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patient-studies/${study.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedStudy(data);
                setShowDetailModal(true);
            }
        } catch (error) {
            console.error('Error fetching study detail:', error);
        }
    };

    const openCancelModal = (study) => {
        setStudyToCancel(study);
        setCancelReason('');
        setShowCancelModal(true);
    };

    const handleCancelStudy = async () => {
        if (!studyToCancel) return;

        try {
            setCancelling(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patient-studies/${studyToCancel.id}/cancel`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: cancelReason })
            });

            if (res.ok) {
                setShowCancelModal(false);
                setStudyToCancel(null);
                setCancelReason('');
                fetchStudies(); // Refresh list
            } else {
                const data = await res.json();
                alert(data.message || 'Error al cancelar el estudio');
            }
        } catch (error) {
            console.error('Error cancelling study:', error);
            alert('Error de conexión');
        } finally {
            setCancelling(false);
        }
    };

    const canCancelStudy = (status) => {
        return ['solicitud_medico', 'solicitado', 'pendiente_muestra', 'muestra_tomada', 'enviado'].includes(status);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderStatusBadge = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG['solicitado'];
        const Icon = config.icon;
        return (
            <span
                className="status-badge"
                style={{ backgroundColor: `${config.color}20`, color: config.color }}
            >
                <Icon size={14} />
                {config.label}
            </span>
        );
    };

    return (
        <div className="patient-studies-container">
            <div className="studies-header">
                <div className="header-title">
                    <FlaskConical size={24} />
                    <h2>Estudios del Paciente</h2>
                    <span className="study-count">{studies.length} estudios</span>
                </div>
                <button className="btn-primary" onClick={onAssignStudy}>
                    <Plus size={18} />
                    Asignar Estudio
                </button>
            </div>

            <div className="studies-filters">
                <div className="filter-group">
                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <option value="">Todos los estados</option>
                        {FILTER_STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <ChevronDown size={16} className="select-icon" />
                </div>

                <div className="filter-group">
                    <input
                        type="date"
                        value={filters.from_date}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                        placeholder="Desde"
                    />
                </div>

                <div className="filter-group">
                    <input
                        type="date"
                        value={filters.to_date}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                        placeholder="Hasta"
                    />
                </div>
            </div>

            <div className="studies-list">
                {loading ? (
                    <div className="loading-state">Cargando estudios...</div>
                ) : studies.length === 0 ? (
                    <div className="empty-state">
                        <FlaskConical size={48} strokeWidth={1} />
                        <p>No hay estudios registrados</p>
                        <button className="btn-secondary" onClick={onAssignStudy}>
                            Asignar primer estudio
                        </button>
                    </div>
                ) : (
                    studies.map(study => (
                        <div key={study.id} className={`study-card status-${study.status}`}>
                            <div className="study-header">
                                {renderStatusBadge(study.status)}
                                <span className="study-category">{study.category}</span>
                            </div>

                            <h3 className="study-name">{study.study_name}</h3>

                            <div className="study-meta">
                                <span>
                                    <strong>Médico:</strong> {study.doctor_name}
                                </span>
                                <span>
                                    <strong>Fecha:</strong> {formatDate(study.requested_at)}
                                </span>
                            </div>

                            {study.special_notes && (
                                <p className="study-notes">
                                    <AlertCircle size={14} />
                                    {study.special_notes}
                                </p>
                            )}

                            <div className="study-actions">
                                <button
                                    className="btn-icon"
                                    onClick={() => viewStudyDetail(study)}
                                    title="Ver detalles"
                                >
                                    <Eye size={16} />
                                </button>

                                {study.pdf_path && (
                                    <a
                                        href={study.pdf_path.startsWith('http') ? study.pdf_path : `${API_URL}/${study.pdf_path}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-result"
                                    >
                                        <Eye size={16} />
                                        Ver Resultado
                                    </a>
                                )}

                                {canCancelStudy(study.status) && (
                                    <button
                                        className="btn-cancel-study"
                                        onClick={() => openCancelModal(study)}
                                        title="Cancelar estudio"
                                    >
                                        <Trash2 size={16} />
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Study Detail Modal */}
            {showDetailModal && selectedStudy && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="modal-content study-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedStudy.study_name}</h2>
                            <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="detail-section">
                                <h4>Estado actual</h4>
                                {renderStatusBadge(selectedStudy.status)}
                            </div>

                            <div className="detail-grid">
                                <div className="detail-item">
                                    <label>Código</label>
                                    <p>{selectedStudy.study_code || '-'}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Categoría</label>
                                    <p>{selectedStudy.category}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Precio</label>
                                    <p>${parseFloat(selectedStudy.price || 0).toFixed(2)}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Tiempo estimado</label>
                                    <p>{selectedStudy.estimated_time || '-'}</p>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Información de la solicitud</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Médico solicitante</label>
                                        <p>{selectedStudy.doctor_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label>Fecha de solicitud</label>
                                        <p>{formatDate(selectedStudy.requested_at)}</p>
                                    </div>
                                        <div className="detail-item">
                                            <label>Prioridad</label>
                                            <p className={`priority-${selectedStudy.priority}`}>
                                                {selectedStudy.priority === 'urgente' ? 'Urgente' : 'Rutina'}
                                            </p>
                                        </div>
                                </div>
                            </div>

                            {selectedStudy.patient_instructions && (
                                <div className="detail-section">
                                    <h4>Indicaciones para el paciente</h4>
                                    <p className="instructions-text">{selectedStudy.patient_instructions}</p>
                                </div>
                            )}

                            {selectedStudy.special_notes && (
                                <div className="detail-section">
                                    <h4>Notas especiales</h4>
                                    <p className="notes-text">{selectedStudy.special_notes}</p>
                                </div>
                            )}

                            {selectedStudy.collected_at && (
                                <div className="detail-section">
                                    <h4>Toma de muestra</h4>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Tomada por</label>
                                            <p>{selectedStudy.collected_by_name}</p>
                                        </div>
                                        <div className="detail-item">
                                            <label>Fecha/hora</label>
                                            <p>{formatDate(selectedStudy.collected_at)}</p>
                                        </div>
                                        <div className="detail-item">
                                            <label>Tipo de muestra</label>
                                            <p>{selectedStudy.sample_type || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(selectedStudy.laboratory_name || selectedStudy.catalog_laboratory) && (
                                <div className="detail-section">
                                    <h4>Laboratorio</h4>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Laboratorio</label>
                                            <p>{selectedStudy.laboratory_name || selectedStudy.catalog_laboratory}</p>
                                        </div>
                                        <div className="detail-item">
                                            <label>Tipo</label>
                                            <p>{selectedStudy.laboratory_type}</p>
                                        </div>
                                        {selectedStudy.tracking_number && (
                                            <div className="detail-item">
                                                <label>No. de seguimiento</label>
                                                <p>{selectedStudy.tracking_number}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedStudy.interpretation && (
                                <div className="detail-section">
                                    <h4>Resultado</h4>
                                    <p className="interpretation-text">{selectedStudy.interpretation}</p>
                                    {selectedStudy.pdf_path && (
                                        <a
                                            href={selectedStudy.pdf_path.startsWith('http') ? selectedStudy.pdf_path : `${API_URL}/${selectedStudy.pdf_path}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-primary result-link"
                                        >
                                            <Eye size={16} />
                                            Ver PDF del resultado
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Study Modal */}
            {showCancelModal && studyToCancel && (
                <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="modal-content cancel-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header cancel-header">
                            <div className="cancel-icon-wrapper">
                                <AlertCircle size={24} />
                            </div>
                            <h2>Cancelar Estudio</h2>
                            <button className="close-btn" onClick={() => setShowCancelModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="cancel-warning">
                                <p>¿Estás seguro de que deseas cancelar este estudio?</p>
                                <div className="study-to-cancel">
                                    <strong>{studyToCancel.study_name}</strong>
                                    <span>Solicitado el {formatDate(studyToCancel.requested_at)}</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Motivo de cancelación (opcional)</label>
                                <textarea
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Ej: Estudio solicitado por error, cambio de diagnóstico..."
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancelling}
                            >
                                No, mantener
                            </button>
                            <button
                                className="btn-danger"
                                onClick={handleCancelStudy}
                                disabled={cancelling}
                            >
                                {cancelling ? 'Cancelando...' : 'Sí, cancelar estudio'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientStudies;
